import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

export const CartContext = createContext({
  items: [],
  addCodebase: () => {},
  addBootkit: () => {},
  increaseItem: () => {},
  decreaseItem: () => {},
  setItemQuantity: () => {},
  removeItem: () => {},
  removeCodebase: () => {},
  clearCart: () => {},
  isInCart: () => false,
  subtotal: 0,
  total: 0,
  codebaseCreditCount: 0,
  codebaseCount: 0,
  unappliedCodebaseCredits: 0,
  itemCount: 0,
});

const CART_STORAGE_KEY = 'mw-cart';

function shouldClearCartAfterCheckout() {
  if (typeof window === 'undefined') {
    return false;
  }

  return new URLSearchParams(window.location.search).get('checkout') === 'success';
}

function normalizePrice(price) {
  const parsed = Number(price);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeQuantity(quantity) {
  const parsed = parseInt(quantity, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function normalizeCodebase(codebase) {
  const productId = String(codebase.codebase_id);

  return {
    cart_key: 'codebase:' + productId,
    product_type: 'codebase',
    product_id: productId,
    quantity: normalizeQuantity(codebase.quantity),
    codebase_id: productId,
    name: codebase.name || 'Untitled codebase',
    designer: codebase.designer || '',
    description: codebase.description || '',
    price: normalizePrice(codebase.price),
    icon: codebase.icon || '',
    nickname: codebase.nickname || '',
    featured_image: codebase.featured_image || '',
  };
}

function normalizeBootkit(bootkit) {
  const productId = String(bootkit.kit_id);

  return {
    cart_key: 'bootkit:' + productId,
    product_type: 'bootkit',
    product_id: productId,
    quantity: normalizeQuantity(bootkit.quantity),
    kit_id: productId,
    name: bootkit.name || 'Untitled bootkit',
    designer: '',
    description: bootkit.description || '',
    flavor: bootkit.flavor || '',
    price: normalizePrice(bootkit.price),
    components: bootkit.components || '',
    codebase_credits: Number(bootkit.codebase_credits || 0),
  };
}

function normalizeSavedCartItem(item) {
  if (item.cart_key) {
    return {
      ...item,
      quantity: normalizeQuantity(item.quantity),
      price: normalizePrice(item.price),
      codebase_credits: Number(item.codebase_credits || 0),
    };
  }

  if (item.codebase_id) {
    return normalizeCodebase(item);
  }

  if (item.kit_id) {
    return normalizeBootkit(item);
  }

  return item;
}

const CartContextProvider = (props) => {
  const [items, setItems] = useState(() => {
    try {
      if (shouldClearCartAfterCheckout()) {
        window.localStorage.removeItem(CART_STORAGE_KEY);
        return [];
      }

      return (JSON.parse(window.localStorage.getItem(CART_STORAGE_KEY)) || []).map(normalizeSavedCartItem);
    } catch (error) {
      return [];
    }
  });

  useEffect(() => {
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addCodebase = useCallback((codebase) => {
    const item = normalizeCodebase(codebase);
    setItems((currentItems) => {
      if (currentItems.some((currentItem) => currentItem.cart_key === item.cart_key)) {
        return currentItems.map((currentItem) => {
          if (currentItem.cart_key !== item.cart_key) {
            return currentItem;
          }

          return {
            ...currentItem,
            quantity: normalizeQuantity(currentItem.quantity) + 1,
          };
        });
      }

      return [...currentItems, item];
    });
  }, []);

  const addBootkit = useCallback((bootkit) => {
    const item = normalizeBootkit(bootkit);
    setItems((currentItems) => {
      if (currentItems.some((currentItem) => currentItem.cart_key === item.cart_key)) {
        return currentItems.map((currentItem) => {
          if (currentItem.cart_key !== item.cart_key) {
            return currentItem;
          }

          return {
            ...currentItem,
            quantity: normalizeQuantity(currentItem.quantity) + 1,
          };
        });
      }

      return [...currentItems, item];
    });
  }, []);

  const increaseItem = useCallback((cartKey) => {
    setItems((currentItems) => currentItems.map((item) => {
      if (item.cart_key !== cartKey) {
        return item;
      }

      return {
        ...item,
        quantity: normalizeQuantity(item.quantity) + 1,
      };
    }));
  }, []);

  const decreaseItem = useCallback((cartKey) => {
    setItems((currentItems) => currentItems.flatMap((item) => {
      if (item.cart_key !== cartKey) {
        return [item];
      }

      const nextQuantity = normalizeQuantity(item.quantity) - 1;
      return nextQuantity > 0 ? [{ ...item, quantity: nextQuantity }] : [];
    }));
  }, []);

  const setItemQuantity = useCallback((cartKey, quantity) => {
    const nextQuantity = normalizeQuantity(quantity);
    setItems((currentItems) => currentItems.map((item) => {
      if (item.cart_key !== cartKey) {
        return item;
      }

      return {
        ...item,
        quantity: nextQuantity,
      };
    }));
  }, []);

  const removeItem = useCallback((cartKey) => {
    setItems((currentItems) => currentItems.filter((item) => item.cart_key !== cartKey));
  }, []);

  const removeCodebase = useCallback((codebaseId) => {
    setItems((currentItems) => currentItems.filter((item) => item.cart_key !== 'codebase:' + String(codebaseId)));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const isInCart = useCallback((productId, productType = 'codebase') => {
    return items.some((item) => item.cart_key === productType + ':' + String(productId));
  }, [items]);

  const codebaseCreditCount = items.reduce((total, item) => {
    return total + (item.product_type === 'bootkit' ? Number(item.codebase_credits || 0) * normalizeQuantity(item.quantity) : 0);
  }, 0);
  const codebaseCount = items.reduce((total, item) => {
    return total + (item.product_type === 'codebase' ? normalizeQuantity(item.quantity) : 0);
  }, 0);
  const unappliedCodebaseCredits = Math.max(codebaseCreditCount - codebaseCount, 0);
  const pricedItems = items.reduce((result, item) => {
    const quantity = normalizeQuantity(item.quantity);

    if (item.product_type !== 'codebase') {
      const itemPrice = normalizePrice(item.price);
      return {
        ...result,
        items: [
          ...result.items,
          {
            ...item,
            quantity,
            original_price: itemPrice,
            final_price: itemPrice,
            line_original_total: itemPrice * quantity,
            line_final_total: itemPrice * quantity,
            credit_applied: false,
            credited_quantity: 0,
          }
        ]
      };
    }

    const itemPrice = normalizePrice(item.price);
    const creditedQuantity = Math.min(result.remainingCredits, quantity);
    const paidQuantity = quantity - creditedQuantity;

    return {
      remainingCredits: result.remainingCredits - creditedQuantity,
      items: [
        ...result.items,
        {
          ...item,
          quantity,
          original_price: itemPrice,
          final_price: paidQuantity > 0 ? itemPrice : 0,
          line_original_total: itemPrice * quantity,
          line_final_total: itemPrice * paidQuantity,
          credit_applied: creditedQuantity > 0,
          credited_quantity: creditedQuantity,
        }
      ]
    };
  }, {
    remainingCredits: codebaseCreditCount,
    items: [],
  }).items;
  const subtotal = items.reduce((total, item) => total + (normalizePrice(item.price) * normalizeQuantity(item.quantity)), 0);
  const total = pricedItems.reduce((sum, item) => sum + normalizePrice(item.line_final_total), 0);
  const itemCount = items.reduce((totalCount, item) => totalCount + normalizeQuantity(item.quantity), 0);

  const value = useMemo(() => ({
    items: pricedItems,
    rawItems: items,
    addCodebase,
    addBootkit,
    increaseItem,
    decreaseItem,
    setItemQuantity,
    removeItem,
    removeCodebase,
    clearCart,
    isInCart,
    subtotal,
    total,
    codebaseCreditCount,
    codebaseCount,
    unappliedCodebaseCredits,
    itemCount,
  }), [pricedItems, items, addCodebase, addBootkit, increaseItem, decreaseItem, setItemQuantity, removeItem, removeCodebase, clearCart, isInCart, subtotal, total, codebaseCreditCount, codebaseCount, unappliedCodebaseCredits, itemCount]);

  return (
    <CartContext.Provider value={value}>
      {props.children}
    </CartContext.Provider>
  );
};

export default CartContextProvider;
